import { useEffect, useState } from 'react';
import API_BASE_URL from 'src/constants';
import { handleUnauthorized } from './authToken';

export interface NotificationResource {
  model: string;
  id: number;
  reference?: string;
}

export interface Notification {
  id: number | null;
  message: string;
  rating: string;
  resource_id: NotificationResource;
  create_date: string;
  is_read: boolean;
  user_id: number;
}

interface NotificationsResponse {
  items: Notification[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

type NotificationType = 'all' | 'read' | 'unread';

const PAGE_SIZE = 50;

const useNotifications = () => {
  const accessToken = localStorage.getItem('access_token');
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<NotificationType>('all');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [allNotificationsFetched, setAllNotificationsFetched] = useState(false);

  // Construct URL for API calls based on notification type
  const buildFetchUrl = (page: number): string => {
    const url = new URL(`${API_BASE_URL}portal/push_notification/stream`);
    url.searchParams.append('page', page.toString());
    url.searchParams.append('size', PAGE_SIZE.toString());
    url.searchParams.append('sort_by', 'create_date');
    url.searchParams.append('sort_order', 'desc');

    const domainFilter = [];

    if (selectedType === 'unread' || selectedType === 'read') {
      domainFilter.push({
        key: 'is_read',
        operator: '=',
        value: selectedType !== 'unread',
      });
    }

    if (selectedCategory) {
      domainFilter.push({
        key: 'resource_id',
        operator: 'like',
        value: selectedCategory,
      });
    }

    const domainFilters = `[${domainFilter
      .map(({ key, value, operator }) => {
        let val = `"${value}"`;
        if (value === false) val = 'False';
        if (value === true) val = 'True';
        return `("${key}", "${operator}", ${val})`;
      })
      .join(',')}]`;

    url.searchParams.append('domain', domainFilters);

    return url.toString();
  };

  // Fetch notifications with parameters
  const fetchNotifications = async (page: number = 1, isFetchMore: boolean = false) => {
    if (isFetchMore && allNotificationsFetched && page === 1) return;

    setLoading(true);
    setError(null);

    try {
      const url = buildFetchUrl(page);
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) handleUnauthorized(accessToken);
        throw new Error(`Failed to fetch notifications: ${response.statusText}`);
      }

      const data: NotificationsResponse = await response.json();
      const newNotifications = page === 1 ? data.items : [...notifications, ...data.items];

      setNotifications(newNotifications);
      setPage(page + 1);
      setAllNotificationsFetched(data.items.length < PAGE_SIZE);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch unread count
  const fetchUnreadCount = async (): Promise<number> => {
    try {
      const response = await fetch(`${API_BASE_URL}portal/push_notification/notifications/number_of_unread`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) handleUnauthorized(accessToken);
        throw new Error('Failed to fetch unread count.');
      }

      const count = await response.json();
      setUnreadCount(count);
      return count;
    } catch (err) {
      console.error(error);
      return 0;
    }
  };

  const setAllNotificationsRead = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}portal/push_notification/notifications/read_all`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        method: 'PUT',
      });

      if (!response.ok) {
        if (response.status === 401) handleUnauthorized(accessToken);
        throw new Error('Failed to mark notification as read.');
      }

      fetchNotifications(1);
      fetchUnreadCount();
      return true;
    } catch (err) {
      console.error(err);
      return false;
    }
  };

  // Set a specific notification as read
  const setNotificationRead = async (notificationId: number): Promise<boolean> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}portal/push_notification/notifications/notification/${notificationId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          method: 'PUT',
        },
      );

      if (!response.ok) {
        if (response.status === 401) handleUnauthorized(accessToken);
        throw new Error('Failed to mark notification as read.');
      }

      // remove notification that was marked as read
      if (selectedType === 'unread') {
        setNotifications(prevNotifications =>
          prevNotifications.filter(notification => notification.id !== notificationId),
        );
      }
      if (selectedType === 'all') {
        // set the status of this message to is_read = true
        setNotifications(prevNotifications =>
          prevNotifications.map(notification => {
            if (notification.id === notificationId) {
              return { ...notification, is_read: true };
            }
            return notification;
          }),
        );
      }

      fetchUnreadCount();
      return true;
    } catch (err) {
      console.error(err);
      return false;
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchNotifications(1);
  }, [selectedType, selectedCategory]);

  useEffect(() => {
    fetchUnreadCount();
  }, []);

  // Load more notifications (pagination)
  const fetchMoreNotifications = () => fetchNotifications(page, true);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    fetchMoreNotifications,
    fetchUnreadCount,
    setNotificationRead,
    allNotificationsFetched,
    setSelectedType,
    setSelectedCategory,
    selectedType,
    selectedCategory,
    setAllNotificationsRead,
  };
};

export default useNotifications;
